import React, { useEffect, useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Table, Input, Tooltip } from "antd";
import { CircularProgress } from "@mui/material";
import { format } from "date-fns";
import {
  Currency,
  callNonStoreApi,
  formatPrice,
} from "../utils/utils";
import {
  GetGroupedSettlementDetails,
  GetOrders,
  VerifyBeneficiary,
  SettleBulk,
} from "../utils/ApiEndpoints";
import SettlementDialog from "../Payouts/SettlementDialog";
import { enqueueSnackbar } from "notistack";
import { Parser } from "@json2csv/plainjs";
import { Download, ContentCopy, CheckCircle, Warning, Info, Close } from "@mui/icons-material";
import { Dialog } from "@mui/material";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Design tokens
const colors = {
  bg: "#fafbfc",
  surface: "#ffffff",
  surfaceHover: "#f8fafc",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  text: "#0f172a",
  textSecondary: "#475569",
  textMuted: "#94a3b8",
  accent: "#6366f1",
  accentLight: "#eef2ff",
  blue: "#2563eb",
  blueBg: "#eff6ff",
  blueBorder: "#bfdbfe",
};

const PageContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${colors.bg};
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 64px;
  background: ${colors.surface};
  border-bottom: 1px solid ${colors.border};
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${colors.blueBg};
  border: 1px solid ${colors.blueBorder};
  border-radius: 8px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.blue};
`;

const PageTitle = styled.h1`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
  letter-spacing: -0.01em;
`;

const StatsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const StatLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StatValue = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 52px;
  background: ${colors.surface};
  border-bottom: 1px solid ${colors.borderLight};
  flex-shrink: 0;
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.textSecondary};
`;

const SearchInput = styled(Input)`
  width: 240px;

  &.ant-input-affix-wrapper {
    background: ${colors.bg};
    border-radius: 8px;
    border: 1px solid ${colors.border};
    padding: 8px 12px;
    font-size: 13px;
    transition: all 0.15s ease;

    &:hover {
      border-color: ${colors.textMuted};
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: ${colors.accent};
      box-shadow: 0 0 0 3px ${colors.accentLight};
    }

    .ant-input {
      background: transparent;
      font-size: 13px;

      &::placeholder {
        color: ${colors.textMuted};
      }
    }
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease;

  .ant-table-wrapper,
  .ant-spin-nested-loading,
  .ant-spin-container,
  .ant-table {
    height: 100%;
  }

  .ant-table-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .ant-table-header {
    flex-shrink: 0;
  }

  .ant-table-body {
    flex: 1;
    overflow-y: auto !important;
  }

  .ant-table-thead > tr > th {
    background: ${colors.surface};
    color: ${colors.textMuted};
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 14px 32px;
    border-bottom: 1px solid ${colors.border};

    &::before {
      display: none !important;
    }

    &.ant-table-column-has-sorters:hover {
      background: ${colors.surfaceHover};
    }
  }

  .ant-table-tbody > tr {
    transition: background 0.1s ease;

    &:hover > td {
      background: ${colors.surfaceHover} !important;
    }

    > td {
      padding: 16px 32px;
      color: ${colors.text};
      border-bottom: 1px solid ${colors.borderLight};
      font-size: 13px;
      vertical-align: middle;
    }

    &:last-child > td {
      border-bottom: none;
    }
  }

  .ant-table-expanded-row > td {
    padding: 0 !important;
    background: ${colors.bg} !important;
  }

  .ant-pagination {
    padding: 16px 32px;
    margin: 0;
    background: ${colors.surface};
    border-top: 1px solid ${colors.border};
  }
`;

const ClientName = styled.span`
  font-weight: 500;
  color: ${colors.text};
`;

const ClientSlug = styled.span`
  font-size: 12px;
  color: ${colors.textMuted};
`;

const IdCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IdText = styled.span`
  font-family: "SF Mono", Monaco, "Consolas", monospace;
  font-size: 12px;
  color: ${colors.textSecondary};
`;

const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: ${colors.textMuted};
  cursor: pointer;
  opacity: 0;
  padding: 0;
  transition: all 0.15s ease;

  tr:hover & {
    opacity: 1;
  }

  &:hover {
    background: ${colors.borderLight};
    color: ${colors.accent};
  }
`;

const OrdersCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  background: ${colors.blueBg};
  color: ${colors.blue};
  border: 1px solid ${colors.blueBorder};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${(p) => (p.$processing ? "#fef3c7" : colors.blueBg)};
  color: ${(p) => (p.$processing ? "#d97706" : colors.blue)};
  border: 1px solid ${(p) => (p.$processing ? "#fde68a" : colors.blueBorder)};
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.accentLight};
    border-color: ${colors.accent};
    color: ${colors.accent};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: ${colors.surface};
      border-color: ${colors.border};
      color: ${colors.textMuted};
    }
  }
`;

const AmountGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Amount = styled.span`
  font-weight: 600;
  color: ${colors.text};
  font-variant-numeric: tabular-nums;
  font-size: 13px;
`;

const AmountSub = styled.span`
  font-size: 11px;
  color: ${colors.textMuted};
`;

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke={colors.textMuted}
    strokeWidth="1.5"
  >
    <circle cx="7" cy="7" r="5" />
    <path d="M11 11l3.5 3.5" />
  </svg>
);

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: ${colors.textMuted};
  font-size: 13px;
  font-weight: 500;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 2px solid ${colors.borderLight};
  border-top-color: ${colors.accent};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const EmptyContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${colors.textMuted};
`;

const EmptyText = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const EmptySubtext = styled.span`
  font-size: 13px;
  color: ${colors.textMuted};
`;

const ExpandedTable = styled.div`
  padding: 8px 32px 16px 64px;

  .ant-table-thead > tr > th {
    background: ${colors.bg};
    padding: 10px 16px;
    font-size: 10px;
  }

  .ant-table-tbody > tr > td {
    padding: 12px 16px;
    background: ${colors.surface};
  }
`;

const BulkSettleDialogContainer = styled.div`
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
`;

const BulkDialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.border};
`;

const BulkDialogTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
`;

const BulkDialogBody = styled.div`
  padding: 20px;
  background: ${colors.bg};
  overflow-y: auto;
  max-height: calc(90vh - 57px);
`;

const BulkCloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.textMuted};
  cursor: pointer;
  &:hover {
    background: ${colors.borderLight};
    color: ${colors.text};
  }
`;

const VerifyBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 500;
  background: ${(p) =>
    p.$type === "success" ? "#d1fae5" : p.$type === "info" ? "#dbeafe" : "#fee2e2"};
  color: ${(p) =>
    p.$type === "success" ? "#065f46" : p.$type === "info" ? "#1e40af" : "#991b1b"};
  border: 1px solid
    ${(p) =>
      p.$type === "success" ? "#a7f3d0" : p.$type === "info" ? "#bfdbfe" : "#fecaca"};
`;

const BulkSection = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const BulkSectionHeader = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${colors.borderLight};
`;

const BulkSectionTitle = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BulkInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid ${colors.borderLight};
  &:last-child { border-bottom: none; }
`;

const BulkInfoLabel = styled.span`
  font-size: 12px;
  color: ${colors.textSecondary};
`;

const BulkInfoValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text};
  font-family: "SF Mono", Monaco, Consolas, monospace;
`;

const MismatchValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #dc2626;
  font-family: "SF Mono", Monaco, Consolas, monospace;
  text-decoration: line-through;
`;

const BulkSubmitBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  background: ${colors.text};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  margin-top: 4px;
  &:hover:not(:disabled) { background: #000; }
  &:disabled { background: ${colors.textMuted}; cursor: not-allowed; }
`;

const SettleAllBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: ${colors.accent};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;
  &:hover { background: #4f46e5; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

function SettlementsReady() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);
  const [bulkSettleModal, setBulkSettleModal] = useState({ open: false, group: null });
  const [bulkVerifyData, setBulkVerifyData] = useState(null);
  const [bulkVerifyLoading, setBulkVerifyLoading] = useState(false);
  const [bulkSettling, setBulkSettling] = useState(false);

  const fetchData = async () => {
    try {
      const res = await callNonStoreApi(
        `${GetGroupedSettlementDetails}?status=Processing,ReadyToProcess`,
      );
      setGroups(res.data.groups || []);
    } catch {
      enqueueSnackbar("Failed to load", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDownloadCSV = async (settlement, brandPageName, e) => {
    e.stopPropagation();
    setDownloadingId(settlement.settlementId);
    try {
      const response = await callNonStoreApi(GetOrders, {
        ordersList: settlement.ordersList,
        userId: settlement.userId,
        settlementId: settlement.settlementId,
      });
      const orders = response.data.orders || [];
      if (!orders.length) {
        enqueueSnackbar("No orders", { variant: "warning" });
        return;
      }

      const list = orders.map((d) => ({
        Customer: d.customerName,
        Offering: d.offeringName,
        "Order Id": d.orderId,
        Date: format(new Date(d.date), "dd MMM yyyy, HH:mm"),
        Amount: formatPrice(
          d.paymentBreakdown?.totalAmount || 0,
          Currency.INR,
          "code",
        ),
        Receivable: formatPrice(d.payoutAmount || 0, Currency.INR, "code"),
        TDS: `${(settlement.tdsPercent || 0) * 100}%`,
        "After TDS": formatPrice(
          (d.payoutAmount || 0) * (1 - (settlement.tdsPercent || 0)),
          Currency.INR,
          "code",
        ),
      }));

      const csv = new Parser().parse(list);
      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${settlement.settlementId}_${brandPageName}.csv`;
      link.click();
      enqueueSnackbar("Downloaded", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed", { variant: "error" });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCopy = async (id, e) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(id);
    enqueueSnackbar("Copied", { variant: "success" });
  };

  const handleBulkSettleClick = async (group) => {
    setBulkSettleModal({ open: true, group });
    setBulkVerifyData(null);
    setBulkVerifyLoading(true);
    try {
      const res = await callNonStoreApi(
        `${VerifyBeneficiary}?brandPageId=${group.brandPageId}`
      );
      setBulkVerifyData(res.data);
    } catch (error) {
      console.error("Verify failed:", error);
      enqueueSnackbar("Failed to verify beneficiary", { variant: "error" });
    } finally {
      setBulkVerifyLoading(false);
    }
  };

  const handleBulkSettleConfirm = async () => {
    const group = bulkSettleModal.group;
    if (!group) return;
    setBulkSettling(true);
    try {
      await callNonStoreApi(SettleBulk, { brandPageId: group.brandPageId });
      enqueueSnackbar(`Bulk settlement initiated for ${group.brandPageName}`, {
        variant: "success",
      });
      setBulkSettleModal({ open: false, group: null });
      fetchData();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || "Bulk settlement failed";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setBulkSettling(false);
    }
  };

  const closeBulkModal = () => {
    if (bulkSettling) return;
    setBulkSettleModal({ open: false, group: null });
    setBulkVerifyData(null);
  };

  const filteredGroups = useMemo(() => {
    if (!searchText.trim()) return groups;
    const s = searchText.toLowerCase();
    return groups.filter(
      (g) =>
        g.brandPageName?.toLowerCase().includes(s) ||
        g.brandPageSlug?.toLowerCase().includes(s) ||
        g.settlements?.some((st) =>
          st.settlementId?.toLowerCase().includes(s) ||
          st.offeringName?.toLowerCase().includes(s),
        ),
    );
  }, [groups, searchText]);

  const stats = useMemo(
    () =>
      groups.reduce(
        (a, g) => ({
          creators: a.creators + 1,
          settlements: a.settlements + (g.settlementCount || 0),
          payout: a.payout + (g.totalAmountAfterTds || 0),
          tds: a.tds + (g.totalTds || 0),
        }),
        { creators: 0, settlements: 0, payout: 0, tds: 0 },
      ),
    [groups],
  );

  const groupColumns = [
    {
      title: "Creator",
      render: (_, g) => (
        <AmountGroup>
          <ClientName>{g.brandPageName}</ClientName>
          {g.brandPageSlug && <ClientSlug>/{g.brandPageSlug}</ClientSlug>}
        </AmountGroup>
      ),
      sorter: (a, b) =>
        (a.brandPageName ?? "").localeCompare(b.brandPageName ?? ""),
    },
    {
      title: "Settlements",
      dataIndex: "settlementCount",
      width: 140,
      render: (count) => <Badge>{count} settlements</Badge>,
      sorter: (a, b) => (a.settlementCount || 0) - (b.settlementCount || 0),
    },
    {
      title: "Payout",
      width: 180,
      render: (_, g) => (
        <AmountGroup>
          <Amount>{formatPrice(g.totalAmountAfterTds || 0, Currency.INR)}</Amount>
          <AmountSub>TDS {formatPrice(g.totalTds || 0, Currency.INR)}</AmountSub>
        </AmountGroup>
      ),
      sorter: (a, b) => (a.totalAmountAfterTds || 0) - (b.totalAmountAfterTds || 0),
    },
    {
      title: "Bulk Settle",
      align: "center",
      width: 140,
      render: (_, g) => (
        <SettleAllBtn
          onClick={(e) => {
            e.stopPropagation();
            handleBulkSettleClick(g);
          }}
        >
          Settle All
        </SettleAllBtn>
      ),
    },
  ];

  const settlementColumns = (brandPageName) => [
    {
      title: "Offering",
      dataIndex: "offeringName",
      render: (name) => <ClientName>{name || "—"}</ClientName>,
    },
    {
      title: "Settlement ID",
      dataIndex: "settlementId",
      width: 160,
      render: (id) => (
        <IdCell>
          <IdText>{id}</IdText>
          <Tooltip title="Copy">
            <CopyBtn onClick={(e) => handleCopy(id, e)}>
              <ContentCopy sx={{ fontSize: 12 }} />
            </CopyBtn>
          </Tooltip>
        </IdCell>
      ),
    },
    {
      title: "Status",
      dataIndex: "settlementStatus",
      width: 130,
      render: (status) => (
        <StatusBadge $processing={status === "Processing"}>
          {status}
        </StatusBadge>
      ),
    },
    {
      title: "Orders",
      dataIndex: "orders",
      width: 160,
      render: (o, r) => (
        <OrdersCell>
          <Badge>{o?.count || 0} orders</Badge>
          <Tooltip title="Download CSV">
            <IconBtn
              onClick={(e) => handleDownloadCSV(r, brandPageName, e)}
              disabled={downloadingId === r.settlementId}
            >
              {downloadingId === r.settlementId ? (
                <CircularProgress size={12} />
              ) : (
                <Download sx={{ fontSize: 14 }} />
              )}
            </IconBtn>
          </Tooltip>
        </OrdersCell>
      ),
    },
    {
      title: "Payout",
      dataIndex: "orders",
      width: 160,
      render: (o) => (
        <AmountGroup>
          <Amount>
            {formatPrice(
              o?.totalAmountAfterTds ?? o?.totalPayoutAfterTds ?? 0,
              Currency.INR,
            )}
          </Amount>
          <AmountSub>TDS {formatPrice(o?.tds || 0, Currency.INR)}</AmountSub>
        </AmountGroup>
      ),
    },
    {
      title: "Action",
      align: "center",
      width: 120,
      render: (_, r) => (
        <SettlementDialog
          settlementId={r.settlementId}
          status={r.settlementStatus}
          updateSettlementDoc={fetchData}
        />
      ),
    },
  ];

  const expandedRowRender = (group) => (
    <ExpandedTable>
      <Table
        columns={settlementColumns(group.brandPageName)}
        dataSource={group.settlements || []}
        rowKey="settlementId"
        pagination={false}
        size="small"
      />
    </ExpandedTable>
  );

  if (loading)
    return (
      <PageContainer>
        <Header>
          <HeaderLeft>
            <StatusIndicator>
              <StatusDot />
            </StatusIndicator>
            <PageTitle>Ready to Process</PageTitle>
          </HeaderLeft>
        </Header>
        <LoadingContainer>
          <Spinner />
          <span>Loading settlements...</span>
        </LoadingContainer>
      </PageContainer>
    );

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <StatusIndicator>
            <StatusDot />
          </StatusIndicator>
          <PageTitle>Ready to Process</PageTitle>
        </HeaderLeft>
        <StatsGroup>
          <StatItem>
            <StatLabel>Creators</StatLabel>
            <StatValue>{stats.creators}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Settlements</StatLabel>
            <StatValue>{stats.settlements}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Payout</StatLabel>
            <StatValue>{formatPrice(stats.payout, Currency.INR)}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>TDS</StatLabel>
            <StatValue>{formatPrice(stats.tds, Currency.INR)}</StatValue>
          </StatItem>
        </StatsGroup>
      </Header>
      <Toolbar>
        <RecordCount>
          {filteredGroups.length}{" "}
          {filteredGroups.length === 1 ? "creator" : "creators"}
        </RecordCount>
        <SearchInput
          placeholder="Search by creator, offering, or ID..."
          prefix={<SearchIcon />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </Toolbar>
      {filteredGroups.length === 0 ? (
        <EmptyContainer>
          <EmptyText>
            {searchText ? "No matching settlements" : "No settlements ready"}
          </EmptyText>
          <EmptySubtext>
            {searchText
              ? "Try a different search term"
              : "Pending settlements will appear here when ready"}
          </EmptySubtext>
        </EmptyContainer>
      ) : (
        <TableContainer>
          <Table
            columns={groupColumns}
            dataSource={filteredGroups}
            rowKey="brandPageId"
            expandable={{ expandedRowRender }}
            pagination={{ pageSize: 20, showSizeChanger: false, size: "small" }}
            scroll={{ y: "calc(100vh - 170px)" }}
          />
        </TableContainer>
      )}

      <Dialog
        open={bulkSettleModal.open}
        onClose={closeBulkModal}
        PaperProps={{
          sx: {
            width: "460px",
            maxWidth: "90vw",
            borderRadius: "12px",
            maxHeight: "90vh",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          },
        }}
      >
        <BulkSettleDialogContainer>
          <BulkDialogHeader>
            <BulkDialogTitle>
              Bulk Settle — {bulkSettleModal.group?.brandPageName}
            </BulkDialogTitle>
            <BulkCloseBtn onClick={closeBulkModal}>
              <Close sx={{ fontSize: 20 }} />
            </BulkCloseBtn>
          </BulkDialogHeader>
          <BulkDialogBody>
            {bulkVerifyLoading ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <CircularProgress size={24} sx={{ color: colors.accent }} />
                <div style={{ marginTop: 12, fontSize: 13, color: colors.textSecondary }}>
                  Verifying beneficiary...
                </div>
              </div>
            ) : bulkVerifyData ? (
              <>
                {bulkVerifyData.matched ? (
                  <VerifyBanner $type="success">
                    <CheckCircle sx={{ fontSize: 18 }} />
                    {bulkVerifyData.message || "Bank details match. Safe to proceed."}
                  </VerifyBanner>
                ) : !bulkVerifyData.beneficiaryExists ? (
                  <VerifyBanner $type="info">
                    <Info sx={{ fontSize: 18 }} />
                    No beneficiary on Cashfree. A new one will be created.
                  </VerifyBanner>
                ) : (
                  <VerifyBanner $type="error">
                    <Warning sx={{ fontSize: 18 }} />
                    {bulkVerifyData.message || "Bank detail mismatch! Settlement blocked."}
                  </VerifyBanner>
                )}

                <BulkSection>
                  <BulkSectionHeader>
                    <BulkSectionTitle>Brand Page Bank Details</BulkSectionTitle>
                  </BulkSectionHeader>
                  <BulkInfoRow>
                    <BulkInfoLabel>Account Holder</BulkInfoLabel>
                    <BulkInfoValue>
                      {bulkVerifyData.brandPageBankDetails?.accountHolderName || "—"}
                    </BulkInfoValue>
                  </BulkInfoRow>
                  <BulkInfoRow>
                    <BulkInfoLabel>Account Number</BulkInfoLabel>
                    <BulkInfoValue>
                      {bulkVerifyData.brandPageBankDetails?.accountNumber || "—"}
                    </BulkInfoValue>
                  </BulkInfoRow>
                  <BulkInfoRow>
                    <BulkInfoLabel>IFSC</BulkInfoLabel>
                    <BulkInfoValue>
                      {bulkVerifyData.brandPageBankDetails?.ifsc || "—"}
                    </BulkInfoValue>
                  </BulkInfoRow>
                </BulkSection>

                {bulkVerifyData.beneficiaryExists && bulkVerifyData.cashfreeBankDetails && (
                  <BulkSection>
                    <BulkSectionHeader>
                      <BulkSectionTitle>Cashfree Beneficiary Details</BulkSectionTitle>
                    </BulkSectionHeader>
                    <BulkInfoRow>
                      <BulkInfoLabel>Account Holder</BulkInfoLabel>
                      {bulkVerifyData.matched ? (
                        <BulkInfoValue>{bulkVerifyData.cashfreeBankDetails.accountHolderName}</BulkInfoValue>
                      ) : (
                        <MismatchValue>{bulkVerifyData.cashfreeBankDetails.accountHolderName}</MismatchValue>
                      )}
                    </BulkInfoRow>
                    <BulkInfoRow>
                      <BulkInfoLabel>Account Number</BulkInfoLabel>
                      {bulkVerifyData.matched ? (
                        <BulkInfoValue>{bulkVerifyData.cashfreeBankDetails.accountNumber}</BulkInfoValue>
                      ) : (
                        <MismatchValue>{bulkVerifyData.cashfreeBankDetails.accountNumber}</MismatchValue>
                      )}
                    </BulkInfoRow>
                    <BulkInfoRow>
                      <BulkInfoLabel>IFSC</BulkInfoLabel>
                      {bulkVerifyData.matched ? (
                        <BulkInfoValue>{bulkVerifyData.cashfreeBankDetails.ifsc}</BulkInfoValue>
                      ) : (
                        <MismatchValue>{bulkVerifyData.cashfreeBankDetails.ifsc}</MismatchValue>
                      )}
                    </BulkInfoRow>
                  </BulkSection>
                )}

                <BulkSection>
                  <BulkSectionHeader>
                    <BulkSectionTitle>Settlement Summary</BulkSectionTitle>
                  </BulkSectionHeader>
                  <BulkInfoRow>
                    <BulkInfoLabel>Settlements</BulkInfoLabel>
                    <BulkInfoValue>{bulkSettleModal.group?.settlementCount || 0}</BulkInfoValue>
                  </BulkInfoRow>
                  <BulkInfoRow>
                    <BulkInfoLabel>Total Payout</BulkInfoLabel>
                    <BulkInfoValue style={{ color: "#059669", fontWeight: 600 }}>
                      {formatPrice(bulkSettleModal.group?.totalAmountAfterTds || 0, Currency.INR)}
                    </BulkInfoValue>
                  </BulkInfoRow>
                  <BulkInfoRow>
                    <BulkInfoLabel>TDS</BulkInfoLabel>
                    <BulkInfoValue style={{ color: "#dc2626" }}>
                      {formatPrice(bulkSettleModal.group?.totalTds || 0, Currency.INR)}
                    </BulkInfoValue>
                  </BulkInfoRow>
                </BulkSection>

                <BulkSubmitBtn
                  onClick={handleBulkSettleConfirm}
                  disabled={
                    bulkSettling ||
                    (bulkVerifyData.beneficiaryExists && !bulkVerifyData.matched)
                  }
                >
                  {bulkSettling ? (
                    <>
                      <CircularProgress size={16} sx={{ color: "white" }} />
                      <span>Processing...</span>
                    </>
                  ) : bulkVerifyData.beneficiaryExists && !bulkVerifyData.matched ? (
                    <>
                      <Warning sx={{ fontSize: 18 }} />
                      <span>Settlement Blocked — Mismatch</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle sx={{ fontSize: 18 }} />
                      <span>Confirm & Settle All ({bulkSettleModal.group?.settlementCount || 0})</span>
                    </>
                  )}
                </BulkSubmitBtn>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0", color: colors.textMuted, fontSize: 13 }}>
                Failed to verify beneficiary. Please close and try again.
              </div>
            )}
          </BulkDialogBody>
        </BulkSettleDialogContainer>
      </Dialog>
    </PageContainer>
  );
}

export default SettlementsReady;
